import { localHourNow } from "../../lib/dashboardTime.js";
import { formatGbpPence } from "../../lib/formatMoney.js";
import type { RetentionData } from "../../types.js";
import { ChartCard } from "../ChartCard.js";
import type { BarChartItem } from "../charts/BarChart.js";
import { BarChart } from "../charts/BarChart.js";

/** Same green as Gross Rev / Revenue (by day). */
const REVENUE_FILL = "#31c48d";

function formatGbpAxisTick(pounds: number): string {
  if (pounds >= 1000) return `£${Math.round(pounds).toLocaleString("en-GB")}`;
  if (pounds >= 10) return `£${Math.round(pounds)}`;
  if (pounds > 0 && pounds < 1) return `£${pounds.toFixed(2)}`;
  return `£${(Math.round(pounds * 10) / 10).toFixed(pounds % 1 === 0 ? 0 : 1)}`;
}

const CRITERIA = `Same amount grain as **Revenue (by day)** / Gross Rev.: sum of effective **payment_confirmed** amounts (£). Date + venue + device filters only (**no Game**). Joined to current **devices** / **venues**, so a deleted device is omitted the same way as the by-day chart. Not the shared **tables** checklist (the dashboard Device filter still applies).

Per bar: reporting-local **hour** (0–23, **reporting_tz**) of the **payment_confirmed** \`client_timestamp\`. Empty hours are 0. Summing the 24 hours matches the by-day totals (not an average per day).

Highlight: current local hour in the reporting timezone. No Raw Events hour filter from this chart.`;

export function RevenueByHourCard({
  rows,
  reportingTimezone,
}: {
  rows: RetentionData["revenueByHour"];
  reportingTimezone: string;
}) {
  const bars: BarChartItem[] = rows.map((row) => ({
    label: String(row.hour),
    value: row.revenuePence / 100,
    fill: REVENUE_FILL,
  }));
  const totalPence = rows.reduce((sum, row) => sum + row.revenuePence, 0);

  return (
    <ChartCard
      title="Revenue (by hour of day)"
      titleRight={
        <span className="shrink-0 text-[12px] font-medium tabular-nums text-text-secondary">
          {formatGbpPence(totalPence)} total
        </span>
      }
      criteria={CRITERIA}
      note="When during the day payments land (local clock). Green matches Revenue (by day). Sum across the range, not an average per day."
    >
      <BarChart
        data={bars}
        fillBars
        fillWidth
        compact
        verbatimAxisLabels
        showYAxis
        highlightIndex={localHourNow(reportingTimezone)}
        highlightLabel="Now (local)"
        formatYAxisTick={formatGbpAxisTick}
        barTooltip={(item) => {
          const hour = Number(item.label);
          const row = rows.find((r) => r.hour === hour);
          const pence = row?.revenuePence ?? Math.round(item.value * 100);
          const n = row?.paymentCount ?? 0;
          return `Local hour ${item.label}: ${formatGbpPence(pence)} · ${n.toLocaleString()} payment_confirmed row${n === 1 ? "" : "s"}. Sum across the selected range. No Raw Events hour filter from this chart.`;
        }}
      />
    </ChartCard>
  );
}
